import { altKeyOnly, noModifierKeys, platformModifierKeyOnly, shiftKeyOnly } from 'ol/events/condition';
import DragBox from 'ol/interaction/DragBox';
import PointerInteraction from 'ol/interaction/Pointer';
import { createOrUpdateFromCoordinates } from 'ol/extent';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Translate from 'ol/interaction/Translate';

import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import { SnapInteraction } from 'cadenza/map/interactions/snap-interaction';
import { ModifyInteraction } from 'cadenza/map/interactions/modify-interaction';
import { SelectOverlayInteraction } from 'cadenza/map/interactions/select-overlay-interaction';
import { createModifyPointStyle, getSelectStyle } from 'cadenza/map/style/select-styles';
import { HIT_TOLERANCE } from 'cadenza/map/map-constants';
import { SketchFeatureProperty as FeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { TranslateByHandleInteraction } from 'cadenza/map/interactions/translate-by-handle-interaction';
import { TranslateEventType } from 'cadenza/map/interactions/translate-event';
import { spawnPolygonMergeStrategyWorker } from 'cadenza/map/polygon-merge-strategy/polygon-merge-strategy';
import { FeatureGeometryEditorEvent } from 'cadenza/map/feature-geometry-editor/feature-geometry-editor-event-bus';
import { isPolygonLike } from 'cadenza/utils/ol-geometry-util';

export class CommonModifyInteractionGroup extends InteractionGroup {

  _select;

  constructor (layer, options = {}) {
    const source = layer.getSource();
    const featureFilter = feature => !feature.get(FeatureProperty.IS_UNSELECTABLE);
    const select = new SelectOverlayInteraction({
      condition: event => ((event.type === MapBrowserEventType.CLICK)),
      toggleCondition: platformModifierKeyOnly,
      hitTolerance: HIT_TOLERANCE,
      filter: featureFilter,
      getSelectStyle,
      layers: [ layer ]
    });
    const dragBoxSelect = createBoxSelectionInteraction(layer, select.getFeatures());
    const modify = createModifyInteraction(options.type, layer, select.getFeatures(), HIT_TOLERANCE, options.map, options.eventBus, options.polygonMergeStrategy);
    const immediateSelect = createImmediateSelectInteraction(layer, select.getFeatures(), HIT_TOLERANCE);
    const translate = createTranslateInteraction(select.getFeatures(), source, options.eventBus, options.type, options.polygonMergeStrategy, HIT_TOLERANCE, featureFilter);
    const snap = new SnapInteraction({ source });
    const interactions = [ select, dragBoxSelect, translate, modify, immediateSelect, snap ];
    if (options.withTranslateByHandle) {
      const translateByHandle = createTranslateByHandleInteraction(select.getFeatures(), source, options.eventBus, options.type, options.polygonMergeStrategy, HIT_TOLERANCE, featureFilter);
      interactions.push(translateByHandle);
    }
    super(...interactions);
    this._select = select;
  }

  getSelectedFeatures () {
    return this._select.getFeatures();
  }

}

export function createBoxSelectionInteraction (layer,
  selectFeatureCollection,
  withAdditionalFeatures = (hitFeatures) => hitFeatures) {
  const dragBox = new DragBox({
    condition: shiftKeyOnly
  });

  dragBox.on('boxend', () => {
    selectFeatureCollection.clear();
    const extent = dragBox.getGeometry().getExtent();
    let hitFeatures = [];
    layer.getSource().forEachFeatureIntersectingExtent(extent, feature => hitFeatures.push(feature));
    hitFeatures = withAdditionalFeatures(hitFeatures);
    hitFeatures = hitFeatures.filter(feature => !feature.get(FeatureProperty.IS_UNSELECTABLE));
    hitFeatures.forEach(feature => selectFeatureCollection.push(feature));
  });

  return dragBox;
}

// Creates an additional select interaction, which selects the features already on pointerdown.
// It allows the user to start modifying the geometry immediately, even if it wasn't selected previously.
export function createImmediateSelectInteraction (
  layer,
  selectFeatureCollection,
  hitTolerance,
  withAdditionalFeatures = hitFeature => [ hitFeature ]
) {
  return new PointerInteraction({
    handleDownEvent: mapBrowserEvent => {
      if (noModifierKeys(mapBrowserEvent)) {
        const pixels = [
          mapBrowserEvent.pixel.map(value => value - hitTolerance / 2),
          mapBrowserEvent.pixel.map(value => value + hitTolerance / 2)
        ];
        const coordinates = pixels.map(pixel => mapBrowserEvent.map.getCoordinateFromPixel(pixel));
        const extent = createOrUpdateFromCoordinates(coordinates);

        let hitFeatures = [];
        layer.getSource().forEachFeatureIntersectingExtent(extent, feature => hitFeatures.push(feature));

        hitFeatures = hitFeatures.filter(feature => !feature.get(FeatureProperty.IS_UNSELECTABLE));

        if (hitFeatures.length > 0 && !hitFeatures.some(feature => selectFeatureCollection.getArray().indexOf(feature) >= 0)) {
          selectFeatureCollection.clear();
          const hitFeature = hitFeatures.pop();
          withAdditionalFeatures(hitFeature).forEach(feature => selectFeatureCollection.push(feature));
          mapBrowserEvent.immediateSelectionHandled = true;
        }
      }
    }
  });
}

function createModifyInteraction (type, layer, features, hitTolerance, map, eventBus = undefined, polygonMergeStrategy = undefined) {
  return new ModifyInteraction({
    type,
    features,
    layer,
    // Insert vertex only with ALT modifier pressed, so we are able to drag the whole line/polyline. Otherwise the modify interaction wins and adds new vertices instead of allowing to drag the geometry.
    insertVertexCondition: event => !event.immediateSelectionHandled && altKeyOnly(event),
    pixelTolerance: hitTolerance,
    style: createModifyPointStyle(),
    map,
    eventBus,
    polygonMergeStrategy
  });
}

function createTranslateByHandleInteraction (features, source, eventBus, geometryType, polygonMergeStrategy, hitTolerance, featureFilter) {
  const interaction = new TranslateByHandleInteraction({
    features,
    hitTolerance,
    filter: featureFilter
  });
  if (!isPolygonLike(geometryType) || !polygonMergeStrategy) {
    return interaction;
  }
  configureMergePolygonsOnTranslateEnd(interaction, source, eventBus, polygonMergeStrategy);
  return interaction;
}

function createTranslateInteraction (features, source, eventBus, geometryType, polygonMergeStrategy, hitTolerance, featureFilter) {
  const interaction = new Translate({
    features,
    hitTolerance,
    filter: featureFilter
  });
  if (!isPolygonLike(geometryType) || !polygonMergeStrategy) {
    return interaction;
  }
  configureMergePolygonsOnTranslateEnd(interaction, source, eventBus, polygonMergeStrategy);
  return interaction;
}

function configureMergePolygonsOnTranslateEnd (interaction, source, eventBus, polygonMergeStrategy) {
  // we only run merging if the polygon was actually moved.
  let translated = false;
  eventBus.on(FeatureGeometryEditorEvent.CHANGE_POLYGON_MERGE_STRATEGY, event => {
    polygonMergeStrategy = event.detail.polygonMergeStrategy;
  });
  interaction.addEventListener(TranslateEventType.TRANSLATING, () => {
    translated = true;
  });
  interaction.addEventListener(TranslateEventType.TRANSLATE_END, (event) => {
    if (!translated) {
      return;
    }
    translated = false;
    mergePolygonsOnTranslateEnd(event.features.getArray(), source, eventBus, polygonMergeStrategy);
  });
}

async function mergePolygonsOnTranslateEnd (translatedPolygonFeatures, source, eventBus, polygonMergeStrategy) {
  const base = source.getFeatures()
    .filter(feature => !translatedPolygonFeatures.includes(feature))
    .map(feature => feature.getGeometry());
  const mergeResult = await spawnPolygonMergeStrategyWorker(polygonMergeStrategy)
    .merge(base, translatedPolygonFeatures);
  source.clear();
  source.addFeatures(mergeResult);
  eventBus.dispatchEvent(FeatureGeometryEditorEvent.INTERACTION_FINISHED);
}
